import { ShieldWarning } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from '@/components/ui/toaster';
import { errorMessage } from '@/lib/api';
import { authApi } from './auth-api';
import { ME_KEY, useSession } from './session';

const pad = (n: number) => String(n).padStart(2, '0');
export const formatCountdown = (seconds: number) => `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;

/**
 * Faixa fixa no topo enquanto o operador age como admin de uma loja. A API manda `me.impersonation`
 * (claim `imp` do token); fora de impersonação não renderiza nada. Publica a própria altura em `--imp-h`
 * para os elementos sticky/fixed do layout descerem abaixo dela.
 */
export function ImpersonationBanner() {
  const { me, setMe, refetch } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [now, setNow] = useState(() => Date.now());
  const [leaving, setLeaving] = useState(false);
  const imp = me?.impersonation ?? null;
  const expiresAt = imp ? new Date(imp.expiresAt).getTime() : 0;
  const left = imp ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : 0;
  const expired = Boolean(imp) && left === 0;

  useEffect(() => {
    if (!imp) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [imp]);

  // venceu: pergunta ao servidor (o token expirado derruba a sessão e o guard manda para o login)
  useEffect(() => {
    if (!expired) return;
    void refetch();
    const t = setInterval(() => void refetch(), 5000);
    return () => clearInterval(t);
  }, [expired, refetch]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!imp || !el) return;
    const publish = () => document.documentElement.style.setProperty('--imp-h', `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--imp-h');
    };
  }, [imp]);

  if (!imp || !me) return null;

  const leave = async () => {
    setLeaving(true);
    try {
      const r = await authApi.exitImpersonation();
      // todo dado em cache pertence à loja impersonada
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
      setMe(await authApi.me());
      navigate(r.home, { replace: true });
    } catch (e) {
      toast.error(errorMessage(e));
      setLeaving(false);
    }
  };

  return (
    <div
      ref={ref}
      role="region"
      aria-label="Acesso de suporte da plataforma"
      data-testid="impersonation-banner"
      className="sticky top-0 z-[60] flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-red-700 px-3 py-2 text-center text-sm font-medium text-white"
    >
      <ShieldWarning className="size-5 shrink-0" weight="bold" aria-hidden />
      <span className="min-w-0 break-words">
        Você está acessando como <strong>{me.user.name}</strong> ({imp.organizationName}) —{' '}
        <span className="tabular-nums" data-testid="impersonation-countdown">
          {expired ? 'sessão encerrada' : `${formatCountdown(left)} restantes`}
        </span>{' '}
        —
      </span>
      <button
        type="button"
        onClick={() => void leave()}
        disabled={leaving}
        className="min-h-9 shrink-0 rounded-control border border-white/70 px-3 py-1 font-semibold hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-70"
      >
        {leaving ? 'Saindo…' : 'Sair'}
      </button>
    </div>
  );
}
